/* =========================================================
   Shain Pro 1.0.0 - core behaviour
   Author: Zakria Rajput
   Vanilla JavaScript only. All listeners are delegated so
   sections re-rendered by the theme editor keep working.
   ========================================================= */
(function () {
  'use strict';

  var Shain = window.ShainPro || {};
  Shain.routes = Shain.routes || {};
  Shain.strings = Shain.strings || {};
  Shain.settings = Shain.settings || {};
  window.ShainPro = Shain;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------------- Utilities ---------------- */
  function on(type, selector, handler) {
    document.addEventListener(type, function (event) {
      var target = event.target instanceof Element ? event.target.closest(selector) : null;
      if (target) handler(event, target);
    });
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function parseHTML(text) {
    return new DOMParser().parseFromString(text, 'text/html');
  }

  Shain.on = on;
  Shain.debounce = debounce;
  Shain.parseHTML = parseHTML;

  /* ---------------- Drawers ---------------- */
  var Drawers = {
    active: null,
    lastFocused: null,

    overlay: function () {
      return document.getElementById('ShainOverlay');
    },

    open: function (drawer) {
      if (!drawer) return;
      if (this.active && this.active !== drawer) this.close(this.active);

      this.lastFocused = document.activeElement;
      this.active = drawer;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');

      var overlay = this.overlay();
      if (overlay) {
        overlay.hidden = false;
        requestAnimationFrame(function () {
          overlay.classList.add('is-visible');
        });
      }

      var panel = drawer.querySelector('.drawer__panel');
      if (panel) panel.focus();

      document.querySelectorAll('[aria-controls="' + drawer.id + '"]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      });
    },

    close: function (drawer) {
      var target = drawer || this.active;
      if (!target) return;

      target.classList.remove('is-open');
      target.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');

      var overlay = this.overlay();
      if (overlay) {
        overlay.classList.remove('is-visible');
        window.setTimeout(function () {
          overlay.hidden = true;
        }, 260);
      }

      document.querySelectorAll('[aria-controls="' + target.id + '"]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }

      this.active = null;
    },

    trapFocus: function (event) {
      if (!this.active || event.key !== 'Tab') return;
      var focusable = this.active.querySelectorAll(FOCUSABLE);
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  Shain.Drawers = Drawers;

  on('click', '[data-menu-open]', function () {
    Drawers.open(document.getElementById('MenuDrawer'));
  });

  on('click', '[data-cart-open]', function (event) {
    event.preventDefault();
    Drawers.open(document.getElementById('CartDrawer'));
  });

  on('click', '[data-drawer-close]', function (event, target) {
    Drawers.close(target.closest('.drawer'));
  });

  on('click', '[data-overlay]', function () {
    Drawers.close();
    closeSearch();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      if (Drawers.active) Drawers.close();
      closeSearch();
      closeDropdowns();
    }
    Drawers.trapFocus(event);
  });

  /* ---------------- Search panel ---------------- */
  function searchPanel() {
    return document.querySelector('[data-search-panel]');
  }

  function openSearch() {
    var panel = searchPanel();
    if (!panel) return;
    panel.hidden = false;
    document.querySelectorAll('[data-search-open]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    });
    var input = panel.querySelector('[data-search-input]');
    if (input) input.focus();
  }

  function closeSearch() {
    var panel = searchPanel();
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    document.querySelectorAll('[data-search-open]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  Shain.closeSearch = closeSearch;

  on('click', '[data-search-open]', function () {
    var panel = searchPanel();
    if (panel && panel.hidden) {
      openSearch();
    } else {
      closeSearch();
    }
  });

  on('click', '[data-search-close]', function () {
    closeSearch();
  });

  /* ---------------- Dropdown menus ---------------- */
  function closeDropdowns(except) {
    document.querySelectorAll('[data-dropdown][open]').forEach(function (item) {
      if (item !== except) item.removeAttribute('open');
    });
  }

  document.addEventListener('click', function (event) {
    var dropdown = event.target instanceof Element ? event.target.closest('[data-dropdown]') : null;
    closeDropdowns(dropdown);
  });

  /* ---------------- Sticky header shadow ---------------- */
  function updateHeaderState() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 8);
  }

  window.addEventListener('scroll', updateHeaderState, { passive: true });
  updateHeaderState();

  /* ---------------- Announcement bar ---------------- */
  var announcementTimers = [];

  function initAnnouncementBars() {
    announcementTimers.forEach(clearInterval);
    announcementTimers = [];

    document.querySelectorAll('[data-announcement-bar]').forEach(function (bar) {
      var slides = Array.prototype.slice.call(bar.querySelectorAll('[data-announcement-slide]'));
      if (slides.length < 2) return;

      var index = 0;
      var speed = (parseInt(bar.dataset.speed, 10) || 5) * 1000;

      function show(next) {
        slides[index].hidden = true;
        slides[index].classList.remove('is-active');
        index = (next + slides.length) % slides.length;
        slides[index].hidden = false;
        slides[index].classList.add('is-active');
      }

      var timer = setInterval(function () {
        show(index + 1);
      }, speed);
      announcementTimers.push(timer);

      var prev = bar.querySelector('[data-announcement-prev]');
      var next = bar.querySelector('[data-announcement-next]');
      if (prev) prev.addEventListener('click', function () { show(index - 1); });
      if (next) next.addEventListener('click', function () { show(index + 1); });
    });
  }

  /* ---------------- Quantity steppers ---------------- */
  function stepQuantity(button, delta) {
    var wrapper = button.closest('.quantity');
    if (!wrapper) return;
    var input = wrapper.querySelector('.quantity__input');
    if (!input) return;

    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 1;

    var value = parseInt(input.value, 10);
    if (isNaN(value)) value = min;

    var next = Math.max(min, value + delta);
    if (next === value) return;

    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  on('click', '[data-quantity-minus]', function (event, target) {
    event.preventDefault();
    stepQuantity(target, -1);
  });

  on('click', '[data-quantity-plus]', function (event, target) {
    event.preventDefault();
    stepQuantity(target, 1);
  });

  /* ---------------- Product gallery ---------------- */
  function showMedia(gallery, mediaId) {
    if (!gallery || !mediaId) return;

    gallery.querySelectorAll('[data-gallery-slide]').forEach(function (slide) {
      var isMatch = slide.dataset.gallerySlide === String(mediaId);
      slide.hidden = !isMatch;
      slide.classList.toggle('is-active', isMatch);
    });

    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      thumb.classList.toggle('is-active', thumb.dataset.galleryThumb === String(mediaId));
    });
  }

  on('click', '[data-gallery-thumb]', function (event, target) {
    event.preventDefault();
    showMedia(target.closest('[data-product-gallery]'), target.dataset.galleryThumb);
  });

  /* ---------------- Variant selection ---------------- */
  function getVariants(container) {
    var script = container.querySelector('[data-variant-data]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  function updatePriceBlock(root, sectionId, productUrl, variantId) {
    var target = root.querySelector('#price-' + sectionId);
    if (!target) return;

    var separator = productUrl.indexOf('?') === -1 ? '?' : '&';
    var url = productUrl + separator + 'variant=' + variantId + '&section_id=' + sectionId;

    fetch(url)
      .then(function (response) {
        return response.ok ? response.text() : Promise.reject(response);
      })
      .then(function (text) {
        var fresh = parseHTML(text).querySelector('#price-' + sectionId);
        if (fresh) target.innerHTML = fresh.innerHTML;
      })
      .catch(function () {
        /* Keep the current price if the request fails. */
      });
  }

  function onVariantChange(container) {
    var variants = getVariants(container);
    if (!variants.length) return;

    var root = container.closest('.shopify-section') || document;
    var selected = Array.prototype.map.call(container.querySelectorAll('[data-variant-option]'), function (select) {
      return select.value;
    });

    var variant = variants.filter(function (item) {
      return item.options.every(function (option, index) {
        return option === selected[index];
      });
    })[0];

    var input = root.querySelector('[data-variant-id]');
    var button = root.querySelector('[data-add-button]');
    var label = root.querySelector('[data-add-button-text]');

    if (!variant) {
      if (button) button.disabled = true;
      if (label) label.textContent = Shain.strings.unavailable || 'Unavailable';
      return;
    }

    if (input) input.value = variant.id;

    if (button) button.disabled = !variant.available;
    if (label) {
      label.textContent = variant.available
        ? Shain.strings.addToCart || 'Add to cart'
        : Shain.strings.soldOut || 'Sold out';
    }

    if (variant.featured_media && variant.featured_media.id) {
      showMedia(root.querySelector('[data-product-gallery]'), variant.featured_media.id);
    }

    var sectionId = container.dataset.sectionId;
    var productUrl = container.dataset.productUrl;

    if (window.history && window.history.replaceState && document.body.classList.contains('template-product')) {
      window.history.replaceState({}, '', productUrl + '?variant=' + variant.id);
    }

    if (sectionId && productUrl) {
      updatePriceBlock(root, sectionId, productUrl, variant.id);
    }
  }

  on('change', '[data-variant-option]', function (event, target) {
    var container = target.closest('[data-variant-selects]');
    if (container) onVariantChange(container);
  });

  /* ---------------- Collection sorting ---------------- */
  on('change', '[data-sort-select]', function (event, target) {
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', target.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ---------------- Share button ---------------- */
  on('click', '[data-share-button]', function (event, target) {
    var url = target.dataset.shareUrl;
    var status = target.parentElement ? target.parentElement.querySelector('[data-share-status]') : null;

    function confirmCopy() {
      if (status) {
        status.textContent = Shain.strings.copied || 'Link copied';
        window.setTimeout(function () {
          status.textContent = '';
        }, 2500);
      }
    }

    if (navigator.share) {
      navigator.share({ url: url, title: document.title }).catch(function () {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(confirmCopy).catch(function () {});
    }
  });

  /* ---------------- Password recovery toggle ---------------- */
  function toggleRecover(showRecover) {
    var recover = document.getElementById('RecoverPassword');
    var login = document.getElementById('CustomerLogin');
    if (!recover || !login) return;
    recover.hidden = !showRecover;
    login.hidden = showRecover;
  }

  on('click', '[data-recover-show]', function (event) {
    event.preventDefault();
    toggleRecover(true);
  });

  on('click', '[data-recover-hide]', function (event) {
    event.preventDefault();
    toggleRecover(false);
  });

  /* ---------------- Address book ---------------- */
  on('click', '[data-address-edit]', function (event, target) {
    event.preventDefault();
    var panel = document.getElementById('EditAddress-' + target.dataset.addressEdit);
    if (panel) panel.hidden = !panel.hidden;
  });

  on('click', '[data-address-cancel]', function (event, target) {
    event.preventDefault();
    var panel = document.getElementById('EditAddress-' + target.dataset.addressCancel);
    if (panel) panel.hidden = true;
  });

  on('click', '[data-confirm-message]', function (event, target) {
    if (!window.confirm(target.dataset.confirmMessage)) {
      event.preventDefault();
    }
  });

  function buildProvinces(select) {
    var target = document.getElementById(select.dataset.provinceTarget);
    var wrapper = document.getElementById(select.dataset.provinceWrapper);
    if (!target) return;

    var option = select.options[select.selectedIndex];
    var raw = option ? option.getAttribute('data-provinces') : null;
    var provinces = [];

    try {
      provinces = raw ? JSON.parse(raw) : [];
    } catch (error) {
      provinces = [];
    }

    target.innerHTML = '';

    if (!provinces.length) {
      if (wrapper) wrapper.hidden = true;
      return;
    }

    if (wrapper) wrapper.hidden = false;
    provinces.forEach(function (province) {
      var element = document.createElement('option');
      element.value = province[0];
      element.textContent = province[1];
      target.appendChild(element);
    });

    if (target.dataset.default) target.value = target.dataset.default;
  }

  function initCountrySelectors() {
    document.querySelectorAll('[data-country-select]').forEach(function (select) {
      if (select.dataset.default) select.value = select.dataset.default;
      buildProvinces(select);
    });
  }

  on('change', '[data-country-select]', function (event, target) {
    buildProvinces(target);
  });

  /* ---------------- Scroll reveal ---------------- */
  var observer = null;

  function initAnimations() {
    if (!document.body.classList.contains('animations-enabled')) return;
    if (!('IntersectionObserver' in window)) return;

    if (!observer) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
      );
    }

    document.querySelectorAll('[data-animate-section]:not(.is-visible)').forEach(function (element) {
      observer.observe(element);
    });
  }

  /* ---------------- Init ---------------- */
  function init() {
    initAnnouncementBars();
    initCountrySelectors();
    initAnimations();
    updateHeaderState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor lifecycle */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
  document.addEventListener('shopify:section:reorder', init);

  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest('[data-announcement-slide]');
    if (!slide) return;
    var bar = slide.closest('[data-announcement-bar]');
    if (!bar) return;
    bar.querySelectorAll('[data-announcement-slide]').forEach(function (item) {
      item.hidden = item !== slide;
      item.classList.toggle('is-active', item === slide);
    });
  });
})();
